import React from 'react';
import useSWR, { useSWRConfig, SWRConfig } from 'swr';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var __SWRConfig = undefined;
var mutateAllKeys = function mutateAllKeys() {
  if (__SWRConfig) {
    var _SWRConfig = __SWRConfig,
        cache = _SWRConfig.cache,
        mutate = _SWRConfig.mutate;
    Array.from(cache.keys()).forEach(function (key) {
      mutate(key);
    });
  }
}; // @plasmicapp/query is optimized for SSR, so we do not revalidate
// automatically upon hydration; as if the data is immutable.

function getPlasmicDefaultSWROptions() {
  return {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  };
}
/**
 * Fetches data asynchronously. This data should be considered immutable for the
 * session -- there is no way to invalidate or re-fetch this data.
 *
 * @param key a unique key for this data fetch; if data already exists under this
 *   key, that data is returned immediately.
 * @param fetcher an async function that resolves to the fetched data.
 * @returns an object with either a "data" key with the fetched data if the fetch
 *   was successful, or an "error" key with the thrown Error if the fetch failed.
 */


function usePlasmicQueryData(key, fetcher) {
  var prepassCtx = React.useContext(PrepassContext);
  var opts = getPlasmicDefaultSWROptions();

  if (prepassCtx) {
    // If we're doing prepass, then we are always in suspense mode, because
    // react-ssr-prepass only works with suspense-throwing data fetching.
    opts.suspense = true;
  }

  var config = useSWRConfig();
  React.useEffect(function () {
    __SWRConfig = config;
  }, [config]);
  var resp = useSWR(key, fetcher, opts);

  if (resp.data) {
    return {
      data: resp.data
    };
  } else if (resp.error) {
    return {
      error: resp.error
    };
  } else {
    return {
      isLoading: true
    };
  }
}
/**
 * Fetches data asynchronously using SWR Hook (https://swr.vercel.app/)
 *
 * @param key a unique key for this data fetch; if data already exists under this
 *   key, that data is returned immediately.
 * @param fetcher an async function that resolves to the fetched data.
 * @param options (optional) an object of options for this hook (https://swr.vercel.app/docs/options).
 * @returns an object with either a "data" key with the fetched data if the fetch
 *   was successful, or an "error" key with the thrown Error if the fetch failed.
 */

function useMutablePlasmicQueryData(key, fetcher, options) {
  var prepassCtx = React.useContext(PrepassContext);

  var opts = _extends({}, getPlasmicDefaultSWROptions(), options);

  if (prepassCtx) {
    opts.suspense = true;
  }

  var config = useSWRConfig();
  React.useEffect(function () {
    __SWRConfig = config;
  }, [config]);
  return useSWR(key, fetcher, opts);
}
function PlasmicQueryDataProvider(props) {
  var children = props.children,
      suspense = props.suspense,
      prefetchedCache = props.prefetchedCache;
  var prepass = React.useContext(PrepassContext);

  if (prepass) {
    // If we're in prepass, then there's already a wrappign SWRConfig;
    // don't interfere with it.
    return React.createElement(React.Fragment, null, children);
  } else {
    return React.createElement(SWRConfig, {
      value: {
        fallback: prefetchedCache != null ? prefetchedCache : {},
        suspense: suspense
      }
    }, children);
  }
}
var PrepassContext = /*#__PURE__*/React.createContext(false);
function PlasmicPrepassContext(props) {
  var cache = props.cache,
      children = props.children;
  return React.createElement(PrepassContext.Provider, {
    value: true
  }, React.createElement(SWRConfig, {
    value: {
      provider: function provider() {
        return cache;
      },
      suspense: true,
      fallback: {}
    }
  }, children));
}

if (typeof window !== 'undefined') {
  var root = window;
  var maybeExistingMutateAllKeys = root.__SWRMutateAllKeys;

  root.__SWRMutateAllKeys = function () {
    mutateAllKeys();

    if (typeof maybeExistingMutateAllKeys === 'function') {
      maybeExistingMutateAllKeys();
    }
  };
}

export { PlasmicPrepassContext, PlasmicQueryDataProvider, useMutablePlasmicQueryData, usePlasmicQueryData };
//# sourceMappingURL=query.esm.js.map
